<template>
  <div>
    <div class="font-weight-bold">Вид етапу</div>
    <div class="text-disabled mb-2">
      Оберіть тип дії, яку учасники повинні виконати на цьому етапі
    </div>

    <v-radio-group v-model="stageCredentials.typeId" hide-details class="mb-5">
      <v-row>
        <v-col cols="6">
          <radio-card
            title="Візування"
            subtitle="Погодження документа"
            :is-active="stageCredentials.typeId === 1"
            :value="1"></radio-card>
        </v-col>
        <v-col cols="6">
          <radio-card
            title="Підписання"
            subtitle="Фінальний підпис"
            :is-active="stageCredentials.typeId === 2"
            :value="2"></radio-card>
        </v-col>
      </v-row>
    </v-radio-group>

    <div class="font-weight-bold">Режим погодження</div>
    <div class="text-disabled mb-2">
      Як саме етап вважатиметься завершеним: достатньо одного учасника чи
      потрібні всі
    </div>

    <v-radio-group
      v-model="stageCredentials.completionTypeId"
      hide-details
      class="mb-5">
      <v-row>
        <v-col cols="6">
          <radio-card
            title="Одним із учасників"
            :is-active="stageCredentials.completionTypeId === 1"
            :value="1"></radio-card>
        </v-col>
        <v-col cols="6">
          <radio-card
            title="Всіма учасниками"
            :is-active="stageCredentials.completionTypeId === 2"
            :value="2"></radio-card>
        </v-col>
      </v-row>
    </v-radio-group>

    <b-label label="Порядковий номер"></b-label>
    <div class="text-disabled mb-2">
      Позиція етапу в маршруті — визначає послідовність виконання
    </div>

    <b-input-number
      v-model="stageCredentials.sequence"
      :decimal="0"
      :min-value="0"
      :max-value="dialog.dialogData.maxSequence"
      :error-messages="getErrorMessages(v$.sequence)"
      type="number">
    </b-input-number>

    <div class="font-weight-bold">Термін погодження</div>
    <div class="text-disabled mb-2">
      Максимальний час, протягом якого учасники мають виконати етап
    </div>
    <div class="d-flex align-center ga-3 mb-2">
      <v-chip
        variant="flat"
        color="white"
        border
        rounded="lg"
        density="comfortable"
        style="min-height: 40px"
        :class="{ selected: durationMultiplier === 0 }"
        @click="durationMultiplier = 0">
        Без терміну
      </v-chip>
      <v-chip
        variant="flat"
        color="white"
        border
        rounded="lg"
        density="comfortable"
        style="min-height: 40px"
        :class="{ selected: durationMultiplier === 1 }"
        @click="durationMultiplier = 1">
        Дні
      </v-chip>
      <v-chip
        variant="flat"
        color="white"
        border
        rounded="lg"
        density="comfortable"
        style="min-height: 40px"
        :class="{ selected: durationMultiplier === 7 }"
        @click="durationMultiplier = 7">
        Тижні
      </v-chip>

      <b-input-number
        v-model="durationRaw"
        :decimal="0"
        :min-value="0"
        hide-details
        :error-messages="getErrorMessages(v$.duration)">
      </b-input-number>
    </div>

    <div class="text-disabled font-size-12">
      За замовчанням — 3 дні з урахуванням вихідних
    </div>
  </div>
</template>

<script lang="ts">
import { BInputNumber, BLabel } from 'best-modules/components'
import RadioCard from '@/components/RadioCard.vue'
import { PropType, reactive, ref, watch } from 'vue'
import { Dialog } from '@/dialog'
import { required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import {
  docTypeRouteStageCreate,
  docTypeRouteStageUpdate,
} from '@/requests/documentType'
import {
  documentRouteStageCreate,
  documentRouteStageUpdate,
} from '@/requests/document'
import { pick } from 'lodash'
import { getErrorMessages } from 'best-modules/utils'

const requests = {
  administration: {
    createStage: docTypeRouteStageCreate,
    updateStage: docTypeRouteStageUpdate,
  },
  document: {
    createStage: documentRouteStageCreate,
    updateStage: documentRouteStageUpdate,
  },
}

export default {
  name: 'DocTypeRouteStage',
  components: { RadioCard, BInputNumber, BLabel },
  props: {
    dialog: {
      type: Object as PropType<Dialog>,
      required: true,
    },
  },
  methods: { getErrorMessages },
  setup(props) {
    const stageCredentials = reactive({
      typeId: 1,
      completionTypeId: 2,
      sequence: props.dialog.dialogData.stage.sequence,
      duration: null as any,
    })
    const rules = {
      typeId: { required },
      completionTypeId: { required },
      sequence: { required },
      duration: { required },
    }
    const v$ = useVuelidate(rules, stageCredentials)

    const durationMultiplier = ref(1)
    const durationRaw = ref(3)

    const setDefaultDuration = (duration: number) => {
      switch (true) {
        case duration === 0:
          durationMultiplier.value = 0
          durationRaw.value = 0
          break
        case duration < 7:
          durationMultiplier.value = 1
          durationRaw.value = duration
          break
        case duration >= 7:
          durationMultiplier.value = 7
          durationRaw.value = duration / 7
          break
      }
    }

    if (props.dialog.action === 'update') {
      console.log(
        's',
        pick(props.dialog.dialogData.stage, [
          'typeId',
          'completionTypeId',
          'sequence',
          'duration',
        ])
      )
      Object.assign(
        stageCredentials,
        pick(props.dialog.dialogData.stage, [
          'typeId',
          'completionTypeId',
          'sequence',
          'duration',
        ])
      )
      setDefaultDuration(props.dialog.dialogData.stage.duration)
    }

    const submit = () => {
      if (props.dialog.action === 'create') {
        return requests[
          props.dialog.dialogData.resource as keyof typeof requests
        ].createStage({
          ...props.dialog.dialogData.stage,
          ...stageCredentials,
        })
      } else {
        return requests[
          props.dialog.dialogData.resource as keyof typeof requests
        ].updateStage(props.dialog.dialogData.stage.id, stageCredentials as any)
      }
    }

    watch(durationMultiplier, multiplier => {
      switch (multiplier) {
        case 0:
          durationRaw.value = 0
          break
        case 1:
          durationRaw.value = 3
          break
        case 7:
          durationRaw.value = 1
      }
    })

    watch(
      durationRaw,
      duration => {
        stageCredentials.duration = durationMultiplier.value * duration
      },
      { immediate: true }
    )

    return { stageCredentials, v$, submit, durationMultiplier, durationRaw }
  },
}
</script>

<style scoped lang="scss">
.selected {
  color: #185fa5 !important;
  background: #e6f1fb !important;
  font-weight: bold;
  border-color: #185fa5;
}
</style>
